import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ethers } from 'ethers';
import { useWeb3 } from '../../context/Web3Context';
import { useUserData } from '../../hooks/useUserData';
import { useContracts } from '../../hooks/useContract';
import { useNotify } from '../../context/NotificationContext';
import { formatUsdc, shortAddress, bnToNumber } from '../../utils/format';
import { NetworkGuard } from '../wallet/NetworkGuard';
import { InlineLoader, ButtonSpinner } from '../common/Loader';
import { UserCard } from './UserCard';
import { Speedometer } from './Speedometer';
import { IncomeBreakdown } from './IncomeBreakdown';
import { RecentActivity } from './RecentActivity';
import { APP_NAME } from '../../config/appConfig';
import { parseError } from '../../utils/errors';
import { sendTx } from '../../utils/txHelpers';

export const Dashboard = () => {
  const { account } = useWeb3();

  if (!account) {
    return (
      <div>
        <div className="user-card" style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: 60, marginBottom: 12 }}>🌌</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: 8,
                        background: 'var(--grad-primary)',
                        WebkitBackgroundClip: 'text', backgroundClip: 'text',
                        WebkitTextFillColor: 'transparent', color: 'transparent' }}>
            Welcome to {APP_NAME}
          </div>
          <div style={{ color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.6 }}>
            Connect your wallet to unlock daily ROI, multi-level rewards, and rank bonuses.
          </div>
        </div>
        <NetworkGuard />
      </div>
    );
  }

  return (
    <NetworkGuard>
      <DashboardInner />
    </NetworkGuard>
  );
};

const DashboardInner = () => {
  const { account } = useWeb3();
  const { loading, userId, isRegistered, basic, extended, pendingRoi, refresh } = useUserData();
  const { viewsR, logicW } = useContracts();
  const notify = useNotify();
  const nav = useNavigate();

  const [investments, setInvestments] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [accruing, setAccruing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!viewsR || !userId) return;
      try {
        const list = await viewsR.getAllUserInvestments(userId);
        if (cancelled) return;
        setInvestments(list);
      } catch (e) {}
    };
    load();
    return () => { cancelled = true; };
  }, [viewsR, userId]);

  // Fetch full activity buffer (up to 100 events) and sum by type for the income breakdown.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!viewsR || !userId) return;
      try {
        const res = await viewsR.getUserActivities(userId, 0, 100);
        if (cancelled) return;
        let roi = ethers.constants.Zero;
        let level = ethers.constants.Zero;
        let rank = ethers.constants.Zero;
        let withdraw = ethers.constants.Zero;
        res.items.forEach((a) => {
          const t = bnToNumber(a.activityType);
          if (t === 2) roi = roi.add(a.amount);
          else if (t === 3) level = level.add(a.amount);
          else if (t === 4) rank = rank.add(a.amount);
          else if (t === 6) withdraw = withdraw.add(a.amount);
        });
        setBreakdown({ roi, level, rank, withdraw });
      } catch (e) {}
    };
    load();
    return () => { cancelled = true; };
  }, [viewsR, userId]);

  if (loading && !basic) return <InlineLoader text="Loading your profile..." />;

  if (!isRegistered) {
    return (
      <div>
        <div className="user-card" style={{ textAlign: 'center', padding: 32 }}>
          <div style={{ fontSize: 50, marginBottom: 12 }}>🚀</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 800, marginBottom: 6 }}>Get Started</div>
          <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 20, lineHeight: 1.5 }}>
            Connected as <b>{shortAddress(account)}</b>. Register with a referrer ID and pick your first package.
          </div>
          <button className="btn btn-primary btn-lg btn-full" onClick={() => nav('/invest')}>
            Register & Invest →
          </button>
        </div>
      </div>
    );
  }

  // Find highest package activated (from real/virtual investments)
  let highestPkg = null;
  if (investments && investments.length > 0) {
    let maxPrice = ethers.constants.Zero;
    investments.forEach((inv) => {
      const isNonLeader = bnToNumber(inv.investmentType) !== 2;
      if (isNonLeader && inv.amount.gt(maxPrice)) {
        maxPrice = inv.amount;
        highestPkg = bnToNumber(inv.packageId);
      }
    });
  }

  // Ceiling: sum of totalIncomeReceived vs sum of maxTotalIncome (across non-leader active/inactive)
  let totalRcv = ethers.constants.Zero;
  let totalCap = ethers.constants.Zero;
  if (investments) {
    investments.forEach((inv) => {
      if (bnToNumber(inv.investmentType) === 2) return;
      totalRcv = totalRcv.add(inv.totalIncomeReceived);
      totalCap = totalCap.add(inv.maxTotalIncome);
    });
  }
  const ceilingPct = !totalCap.isZero()
    ? Number(totalRcv.mul(10000).div(totalCap)) / 100
    : 0;

  // Handlers
  const handleClaim = async () => {
    if (!logicW || !basic || basic.claimableAmount.isZero()) {
      notify.warning('Nothing to claim');
      return;
    }
    setClaiming(true);
    try {
      await sendTx(logicW, 'claimIncome', [], { notify, label: 'Claim' });
      refresh();
    } catch (e) {
      notify.error(parseError(e));
    } finally { setClaiming(false); }
  };

  const handleAccrue = async () => {
    if (!logicW) return;
    setAccruing(true);
    try {
      await sendTx(logicW, 'accrueROI', [userId], { notify, label: 'Sync ROI' });
      refresh();
    } catch (e) {
      notify.error(parseError(e));
    } finally { setAccruing(false); }
  };

  const copyRef = () => {
    const url = `${window.location.origin}?ref=${userId}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url);
      notify.success('Referral link copied!');
    } else {
      notify.info(url);
    }
  };

  // Income breakdown — summed from user's activity buffer (last 100 events on-chain).
  const incomeItems = [
    { key: 'roi',      label: 'ROI Income',      icon: '📈', amount: breakdown?.roi,      color: 'linear-gradient(90deg, #00e5ff, #a855ff)' },
    { key: 'level',    label: 'Level Income',    icon: '🔗', amount: breakdown?.level,    color: 'linear-gradient(90deg, #a855ff, #ff2d92)' },
    { key: 'rank',     label: 'Rank Income',     icon: '🏆', amount: breakdown?.rank,     color: 'linear-gradient(90deg, #ffb800, #ff2d92)' },
    { key: 'withdraw', label: 'Withdraw Income', icon: '💸', amount: breakdown?.withdraw, color: 'linear-gradient(90deg, #00ff9d, #00e5ff)' }
  ];

  const refLink = `${window.location.origin}?ref=${userId}`;

  return (
    <div>
      {/* 1. USER CARD */}
      <UserCard
        userId={userId}
        wallet={account}
        uplineId={extended ? bnToNumber(extended.referrerId) : null}
        highestPackage={highestPkg}
      />

      {/* 2. LIFETIME EARNINGS */}
      <div className="lifetime-card">
        <div className="lifetime-icon">💰</div>
        <div className="lifetime-body">
          <div className="lifetime-label">Lifetime Earnings</div>
          <div className="lifetime-amount">${formatUsdc(basic?.totalIncome)}</div>
        </div>
      </div>

      {/* 3. AVAILABLE BALANCE + CLAIM */}
      <div className="balance-card">
        <div className="balance-label">
          <span className="dot dot-green" />
          Available Balance
        </div>
        <div className="balance-amount">
          ${formatUsdc(basic?.claimableAmount)}
          <span className="currency">USDC</span>
        </div>
        <div className="balance-sub">
          Paid out in ZENOVA · 8% admin fee + 2% level fee apply
        </div>
        <div className="balance-actions">
          <button className="btn btn-primary btn-full"
            onClick={handleClaim}
            disabled={claiming || !basic || basic.claimableAmount.isZero()}>
            {claiming && <ButtonSpinner />}
            {claiming ? 'Claiming...' : '💎 Claim Now'}
          </button>
        </div>
      </div>

      {/* 4. TOTAL INVESTED / EARNED / PENDING ROI + SYNC */}
      <div className="stats-grid-compact">
        <div className="stat-mini cyan">
          <div className="stat-mini-label"><span className="stat-mini-icon">💰</span>Total Invested</div>
          <div className="stat-mini-value">${formatUsdc(basic?.totalInvestment)}</div>
        </div>
        <div className="stat-mini pink">
          <div className="stat-mini-label"><span className="stat-mini-icon">📊</span>Active Investment</div>
          <div className="stat-mini-value">${formatUsdc(basic?.activeInvestment)}</div>
        </div>
        <div className="stat-mini green">
          <div className="stat-mini-label"><span className="stat-mini-icon">📈</span>Total Earned</div>
          <div className="stat-mini-value">${formatUsdc(basic?.totalIncome)}</div>
        </div>
        <div className="stat-mini gold">
          <div className="stat-mini-label"><span className="stat-mini-icon">⏳</span>Available ROI</div>
          <div className="stat-mini-value">${formatUsdc(pendingRoi)}</div>
        </div>
      </div>

      <button className="btn btn-secondary btn-full mb-16" onClick={handleAccrue} disabled={accruing}>
        {accruing && <ButtonSpinner />}
        {accruing ? 'Syncing...' : '🔄 Sync ROI to Balance'}
      </button>

      {/* 5. REFERRAL LINK */}
      <div className="section-card">
        <div className="section-card-title">
          <span><span className="icon">🔗</span> Your Referral</span>
          <span className="ref-code">ID: {userId}</span>
        </div>
        <div className="ref-link-mobile">
          <input readOnly value={refLink} onFocus={(e) => e.target.select()} />
          <button className="btn btn-primary btn-sm" onClick={copyRef}>Copy</button>
        </div>
      </div>

      {/* 6. TEAM STATS */}
      <div className="section-card">
        <div className="section-card-title">
          <span><span className="icon">👥</span> Team Overview</span>
        </div>
        <div className="stats-grid-compact" style={{ marginBottom: 0 }}>
          <div className="stat-mini cyan">
            <div className="stat-mini-label">Direct Referrals</div>
            <div className="stat-mini-value">{extended?.directReferrals?.toString() || '0'}</div>
          </div>
          <div className="stat-mini green">
            <div className="stat-mini-label">Active Directs</div>
            <div className="stat-mini-value">{extended?.activeDirects?.toString() || '0'}</div>
          </div>
          <div className="stat-mini pink">
            <div className="stat-mini-label">Team Members</div>
            <div className="stat-mini-value">{extended?.activeMembersDownline?.toString() || '0'}</div>
          </div>
          <div className="stat-mini gold">
            <div className="stat-mini-label">Team Volume</div>
            <div className="stat-mini-value">${formatUsdc(extended?.teamVolume)}</div>
          </div>
          <div className="stat-mini cyan">
            <div className="stat-mini-label">Direct Business</div>
            <div className="stat-mini-value">${formatUsdc(basic?.totalInvestment)}</div>
          </div>
          <div className="stat-mini pink">
            <div className="stat-mini-label">Expense Wallet</div>
            <div className="stat-mini-value">${formatUsdc(basic?.expenseBalance)}</div>
          </div>
        </div>
      </div>

      {/* 7. INCOME BREAKDOWN */}
      <div className="section-card">
        <div className="section-card-title">
          <span><span className="icon">📊</span> Income Breakdown</span>
          <span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>Recent · lifetime total below</span>
        </div>
        <IncomeBreakdown items={incomeItems} total={basic?.totalIncome} />
      </div>

      {/* 8. CEILING SPEEDOMETER */}
      <div className="section-card">
        <div className="section-card-title">
          <span><span className="icon">🚀</span> Income Ceiling</span>
          <span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>All Investments · 4x cap</span>
        </div>
        <Speedometer
          percent={ceilingPct}
          current={formatUsdc(totalRcv)}
          ceiling={formatUsdc(totalCap)}
          label="Utilized"
        />
      </div>

      {/* 9. RECENT ACTIVITY */}
      <div className="section-card">
        <div className="section-card-title">
          <span><span className="icon">📜</span> Recent Activity</span>
        </div>
        <RecentActivity userId={userId} />
      </div>
    </div>
  );
};
